export function extractProperties(object, properties) {
	if(Array.isArray(object)) {
		return object.map(o => extractProperties(o, properties));
	} else {
		const props = {};

		Object.keys(properties).forEach(property => {
			if(object.hasOwnProperty(property)) {
				const value = properties[property];
				if(value) {
					if(typeof value === "function") {
						props[property] = value(object[property]);
					} else if(typeof value === "object") {
						props[property] = extractProperties(object[property], value);
					} else {
						props[property] = object[property];
					}
				}
			}
		});

		return props;
	}
}

/**
 * Will return the value of the object property by path
 *
 * E.g.:
 * path = user.metadata.location
 * searchObject = {
 *      user: {
 *          metadata: {
 *              location: "Berlin"
 *          }
 *      }
 * }
 * returns = "Berlin" or undefined when the path is not found
 *
 * @param {string} path
 * @param {Object} object
 * @return {*}
 */
export function getValueByPath(path, object) {
	const pathProps = path.split(".");
	let prop = object;

	for(let i = 0, len = pathProps.length; i < len; i++) {
		if(prop === undefined)
			return undefined;

		// Returns undefined because a null value in between means the path doesn't exists.
		if(prop === null)
			return undefined;

		prop = prop[pathProps[i]];
	}

	return prop;
}

/**
 * Will set the value on a specific path, sub-properties will be created if not exists.
 *
 * E.g.:
 * path = user.metadata.location
 * value = "Berlin"
 * searchObject = {}
 * turns into:
 * {
 *      user: {
 *          metadata: {
 *              location: "Berlin"
 *          }
 *      }
 * }
 *
 * @param {string} path
 * @param value
 * @param {Object} object
 * @private
 */
export function setValueByPath(path, value, object) {
	const pathProps = path.split(".");
	for(let i = 0, len = pathProps.length, prop = object; i < len; i++) {
		const propName = pathProps[i];
		if((i+1) < len) {
			prop = prop.hasOwnProperty(propName) && prop[propName] !== null ? prop[propName] : (prop[propName] = {});
		} else {
			// Reached end of path. Set value
			prop[propName] = value;
			return prop[propName];
		}
	}
}

/**
 * Will removes the end key of the path and any empty key
 *
 * E.g.1:
 * path = user.metadata.location
 * object = {
 *      user: {
 *          metadata: {
 *              location: "Berlin"
 *          }
 *      }
 * }
 * turns into:
 * object = {}
 *
 * E.g.2:
 * path = user.metadata.location
 * object = {
 *      user: {
 *          metadata: {
 *              location: "Berlin",
 *              timezone: "Europe/Berlin"
 *          }
 *      }
 * }
 * turns into:
 * object = {
 *      user: {
 *          metadata: {
 *              timezone: "Europe/Berlin"
 *          }
 *      }
 * }
 *
 * @param {string} path
 * @param {Object} object
 * @return {Boolean} Returns true if the removing of the path was successful. false otherwise.
 * @private
 */
export function removeValueByPath(path, object) {
	const keys = path.split(".");

	let previous = [], current = object;
	for(let i = 0, len = keys.length; i < len; i++) {
		if(current.hasOwnProperty(keys[i])) {
			const isLast = i === keys.length-1;
			if(isLast) {
				while(i >= 0) {
					// Continue deleting previous as long object is empty
					delete current[keys[i]];
					if(Object.keys(current).length === 0) {
						i--;
						current = previous[i];
					} else
						break; // No further empty objects
				}

				return true;
			} else {
				previous.push(current);
				current = current[keys[i]];
			}
		} else
			return false;
	}

	return false;
}

/**
 * Deep copies the value.
 *
 * @param value
 * @return {any}
 */
export function deepCopy(value) {
	if(!value) return value;
	return JSON.parse(JSON.stringify(value));
}

/**
 * Returns true if the two objects are equal. (Also accepts primitives).
 *
 * @param o1
 * @param o2
 * @return {boolean}
 */
export function objectsEqual(o1, o2) {
	return typeof o1 === "object" && (o1 && o1.constructor === Object) && Object.keys(o1).length > 0
		? Object.keys(o1).length === Object.keys(o2).length && Object.keys(o1).every(p => objectsEqual(o1[p], o2[p]))
		: o1 === o2;
}